import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Key, CheckCircle, XCircle, Loader, Copy, RefreshCw, RotateCcw, AlertTriangle, Search, ExternalLink, Settings } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';
import MetaConnectButton from '../../components/Meta/MetaConnectButton';

// Configurações › Integrações: cada serviço conectado ao CRM vira um cartão (selo, estado e resumo), agrupado por
// categoria. "Gerenciar" abre o painel da integração num modal ou leva à tela própria dela (Gestor de Tráfego,
// WhatsApp, Portais). O estado de cada cartão vem do endpoint de status que a integração já tinha.

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const quando = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : null);

function CopyLinha({ texto }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center gap-2 mt-1">
      <code className="flex-1 bg-gray-100 px-2 py-1.5 rounded text-xs break-all">{texto}</code>
      <button type="button" onClick={() => { navigator.clipboard.writeText(texto); setOk(true); setTimeout(() => setOk(false), 1500); }}
        className="flex items-center gap-1 text-xs text-blue-600 hover:underline whitespace-nowrap"><Copy size={12} /> {ok ? 'Copiado' : 'Copiar'}</button>
    </div>
  );
}

/* ── Kling (MCP com login OAuth feito pelo CRM — imagens/vídeos do Marketing › Criar Imagens) ── */
function KlingPainel() {
  const [info, setInfo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [trabalhando, setTrabalhando] = useState(false);
  const [msg, setMsg] = useState(() => {
    const r = new URLSearchParams(window.location.search).get('kling');
    return r === 'ok' ? { ok: true, t: 'Conta do Kling conectada.' } : r ? { ok: false, t: r === 'negado' ? 'Login cancelado no Kling.' : 'Não foi possível concluir o login — tente de novo.' } : null;
  });

  const load = useCallback(async () => {
    setCarregando(true);
    try { const { data } = await api.get('/marketing/imagens/kling'); setInfo(data.data); }
    catch (e) { setInfo({ conectado: false, erro: e.response?.status === 403 ? 'Somente administradores veem esta integração' : (e.response?.data?.error || e.message) }); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const conectar = async () => {
    setTrabalhando(true); setMsg(null);
    try { const { data } = await api.post('/marketing/imagens/kling/login'); window.location.href = data.data.url; }
    catch (e) { setMsg({ ok: false, t: e.response?.data?.error || e.message }); setTrabalhando(false); }
  };
  const desconectar = async () => {
    if (!window.confirm('Desconectar a conta do Kling?')) return;
    setTrabalhando(true);
    try { const { data } = await api.delete('/marketing/imagens/kling/login'); setInfo(data.data); setMsg(null); }
    catch (e) { setMsg({ ok: false, t: e.response?.data?.error || e.message }); }
    finally { setTrabalhando(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500">Conecta a conta do Kling AI por login (MCP oficial). Disponível no Criar Imagens como provedor "Kling".</p>
        </div>
        <button onClick={load} disabled={carregando} className="text-gray-400 hover:text-gray-600" title="Atualizar"><RefreshCw size={16} className={carregando ? 'animate-spin' : ''} /></button>
      </div>
      {!info ? <Loader size={16} className="text-gray-400 animate-spin" /> : info.conectado ? (
        <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle size={14} className="text-green-600" /> Conta do Kling conectada</p>
      ) : (
        <p className="text-sm text-red-600 flex items-center gap-2"><XCircle size={14} /> {info.erro || 'Não conectado'}</p>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={conectar} disabled={trabalhando} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {trabalhando ? <Loader size={14} className="animate-spin" /> : <Key size={14} />} {info?.conectado ? 'Conectar outra conta' : 'Conectar conta'}
        </button>
        {info?.conectado && <button onClick={desconectar} disabled={trabalhando} className="text-sm text-gray-500 hover:text-red-600">Desconectar</button>}
        {msg && <span className={`text-xs ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>{msg.t}</span>}
      </div>
      <p className="text-[11px] text-gray-400">Você entra no site do Kling e volta sozinho para o CRM. Os créditos usados são os da conta conectada.</p>
    </div>
  );
}

/* ── Higgsfield (CLI do servidor — imagens/vídeos do Marketing › Criar Imagens) ── */
function HiggsfieldPainel() {
  const [info, setInfo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [login, setLogin] = useState(null);        // { url } enquanto o login está em andamento
  const [colada, setColada] = useState('');
  const [trabalhando, setTrabalhando] = useState(false);
  const [msg, setMsg] = useState(null);

  const iniciar = async () => {
    setTrabalhando(true); setMsg(null); setColada('');
    try { const { data } = await api.post('/marketing/imagens/higgsfield/login'); setLogin(data.data); }
    catch (e) { setMsg({ ok: false, t: e.response?.data?.error || e.message }); }
    finally { setTrabalhando(false); }
  };
  const concluir = async () => {
    setTrabalhando(true); setMsg(null);
    try {
      const { data } = await api.post('/marketing/imagens/higgsfield/login/concluir', { url: colada });
      setInfo(data.data); setLogin(null); setColada('');
      setMsg({ ok: true, t: `Conta conectada: ${data.data.email || 'ok'}` });
    } catch (e) { setMsg({ ok: false, t: e.response?.data?.error || e.message }); }
    finally { setTrabalhando(false); }
  };

  const load = useCallback(async () => {
    setCarregando(true);
    try { const { data } = await api.get('/marketing/imagens/higgsfield'); setInfo(data.data); }
    catch (e) { setInfo({ conectado: false, erro: e.response?.status === 403 ? 'Somente administradores veem esta integração' : (e.response?.data?.error || e.message) }); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500">Usa a CLI oficial do Higgsfield instalada no servidor (sem chave de API). Disponível no Criar Imagens como provedor "Higgsfield".</p>
        </div>
        <button onClick={load} disabled={carregando} className="text-gray-400 hover:text-gray-600" title="Atualizar"><RefreshCw size={16} className={carregando ? 'animate-spin' : ''} /></button>
      </div>
      {!info ? <Loader size={16} className="text-gray-400 animate-spin" /> : info.conectado ? (
        <p className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
          <CheckCircle size={14} className="text-green-600" /> Conectado como <b>{info.email}</b>
          {info.plano && <span>· plano {info.plano}</span>}
          {info.creditos != null && <span>· <b>{info.creditos}</b> créditos</span>}
        </p>
      ) : (
        <p className="text-sm text-red-600 flex items-center gap-2"><XCircle size={14} /> {info.erro || 'CLI não conectada'}</p>
      )}
      {!login ? (
        <div className="flex items-center gap-3">
          <button onClick={iniciar} disabled={trabalhando} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {trabalhando ? <Loader size={14} className="animate-spin" /> : <Key size={14} />} {info?.conectado ? 'Conectar outra conta' : 'Conectar conta'}
          </button>
          {msg && <span className={`text-xs ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>{msg.t}</span>}
        </div>
      ) : (
        <div className="space-y-2 border border-blue-100 bg-blue-50/50 rounded-lg p-3">
          <p className="text-sm text-gray-700"><b>1.</b> <a href={login.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">Abra este link</a> e entre com a conta Higgsfield que quer usar.</p>
          <p className="text-sm text-gray-700"><b>2.</b> Ao final o navegador vai para uma página de erro em <code>localhost:8765/callback…</code> — é esperado. Copie a URL inteira da barra de endereço e cole aqui:</p>
          <input value={colada} onChange={(e) => setColada(e.target.value)} placeholder="http://localhost:8765/callback?code=…&state=…" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <div className="flex items-center gap-3">
            <button onClick={concluir} disabled={trabalhando || !colada.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {trabalhando ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />} Concluir login
            </button>
            <button onClick={async () => { try { const { data } = await api.delete('/marketing/imagens/higgsfield/login'); setInfo(data.data); } catch (_) { /* já encerrado */ } setLogin(null); setMsg(null); }} className="text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
            {msg && <span className={`text-xs ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>{msg.t}</span>}
          </div>
          <p className="text-[11px] text-gray-400">O link vale por 10 minutos e uma única vez. A conta atual só é substituída quando o login novo termina com sucesso.</p>
        </div>
      )}
      <p className="text-[11px] text-gray-400">Cada imagem gasta poucos créditos e cada vídeo bem mais (ex.: Seedance 5 s ≈ 35). Os créditos são os da conta conectada acima.</p>
    </div>
  );
}

/* ── DWV Integration Card ──────────────────────────────────────────── */
function DwvPainel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [copied, setCopied] = useState('');
  const [dwvApiKey, setDwvApiKey] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/dwv/config');
      setConfig(data.data);
      setDwvApiKey(data.data.dwv_api_key || '');
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRegenerate() {
    if (!window.confirm('Gerar um novo token invalida o token atual. O suporte DWV precisará ser notificado. Continuar?')) return;
    setRegenerating(true);
    try {
      const { data } = await api.post('/dwv/regenerate');
      setConfig(c => ({ ...c, webhook_token: data.data.webhook_token }));
    } catch (_) {}
    finally { setRegenerating(false); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.post('/dwv/config', { dwv_api_key: dwvApiKey });
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (_) {}
    finally { setSaving(false); }
  }

  function copy(text, key) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  }

  async function handleSync() {
    if (!window.confirm('Isso vai acessar o DWV, buscar o status de todas as unidades e atualizar o banco de dados. Pode demorar até 3 minutos. Continuar?')) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post('/dwv/sync');
      setSyncResult({ ok: true, data: data.data });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Erro desconhecido';
      setSyncResult({ ok: false, error: msg });
    } finally {
      setSyncing(false);
    }
  }

  if (loading) return <div className="py-6 text-center text-gray-400 text-sm">Carregando config DWV...</div>;

  return (
    <div className="space-y-5">
      {/* Informações para o suporte DWV */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-4">
        <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Dados para informar ao suporte DWV</p>

        {/* URL do Webhook */}
        <div>
          <p className="text-xs text-gray-500 mb-1">URL do Webhook</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all">
              {config?.webhook_url}
            </code>
            <button onClick={() => copy(config.webhook_url, 'url')}
              className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 whitespace-nowrap">
              <Copy size={13} /> {copied === 'url' ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
        </div>

        {/* Token */}
        <div>
          <p className="text-xs text-gray-500 mb-1">Token de autenticação (header)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all select-all">
              {config?.webhook_token}
            </code>
            <button onClick={() => copy(config.webhook_token, 'token')}
              className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 whitespace-nowrap">
              <Copy size={13} /> {copied === 'token' ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            O DWV deve enviar este token no header <code className="bg-gray-100 px-1 rounded">X-DWV-Token</code> ou <code className="bg-gray-100 px-1 rounded">Authorization: Bearer</code>
          </p>
        </div>

        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
          <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
          {regenerating ? 'Gerando...' : 'Gerar novo token'}
        </button>
      </div>

      {/* Botão Atualizar com DWV */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Sincronização Manual</p>
            <p className="text-xs text-gray-400">Busca o status atual de todas as unidades no DWV e atualiza o banco de dados</p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
          >
            {syncing
              ? <><Loader size={15} className="animate-spin" /> Sincronizando...</>
              : <><RotateCcw size={15} /> Atualizar com DWV</>
            }
          </button>
        </div>

        {syncing && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-700">
            <p className="flex items-center gap-2"><Loader size={14} className="animate-spin" /> Acessando DWV e coletando dados... isso pode levar até 3 minutos.</p>
          </div>
        )}

        {syncResult && !syncing && (
          syncResult.ok ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-green-700">
                <CheckCircle size={16} /> Sincronização concluída em {Math.round((syncResult.data?.duration_ms || 0) / 1000)}s
              </p>
              <div className="space-y-1.5">
                {(syncResult.data?.empreendimentos || []).map(emp => (
                  <div key={emp.nome} className="flex items-center justify-between bg-white border border-green-100 rounded-lg px-3 py-2 text-xs">
                    <span className="font-medium text-gray-800">{emp.nome}</span>
                    <span className="flex gap-3 text-gray-500">
                      <span className="text-red-600 font-medium">{emp.vendidas} vendidas</span>
                      <span className="text-yellow-600 font-medium">{emp.reservadas} reservadas</span>
                      <span className="text-green-600 font-medium">{emp.disponiveis} disponíveis</span>
                    </span>
                  </div>
                ))}
              </div>
              {(syncResult.data?.warnings || []).length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 text-xs text-yellow-700">
                  <p className="flex items-center gap-1 font-medium mb-1"><AlertTriangle size={12} /> Avisos:</p>
                  {syncResult.data.warnings.map((w, i) => <p key={i}>• {w}</p>)}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
              <p className="flex items-center gap-2 font-semibold text-red-700"><XCircle size={16} /> Erro na sincronização</p>
              <p className="text-red-600 mt-1 text-xs">{syncResult.error}</p>
            </div>
          )
        )}
      </div>

      {/* Mapeamento de status (informativo) */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-2">Mapeamento de status DWV → CRM</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {[
            { dwv: 'available / disponivel', crm: 'Disponível', cls: 'bg-green-50 text-green-700' },
            { dwv: 'reserved / reservado',   crm: 'Reservada',  cls: 'bg-yellow-50 text-yellow-700' },
            { dwv: 'sold / vendido',          crm: 'Vendida',    cls: 'bg-red-50 text-red-700' },
          ].map(m => (
            <div key={m.crm} className={`rounded-lg p-2.5 ${m.cls}`}>
              <p className="font-mono opacity-70">{m.dwv}</p>
              <p className="font-semibold mt-0.5">→ {m.crm}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Instruções */}
      <details className="group">
        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
          <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
          Como configurar no lado DWV?
        </summary>
        <div className="mt-3 text-xs text-gray-500 space-y-1.5 bg-gray-50 rounded-lg p-3 leading-relaxed">
          <p><strong>1.</strong> Acesse o painel DWV → Configurações → Integrações → Webhook</p>
          <p><strong>2.</strong> Informe a <strong>URL do Webhook</strong> acima</p>
          <p><strong>3.</strong> Informe o <strong>Token</strong> acima (DWV enviará no header de cada requisição)</p>
          <p><strong>4.</strong> Na aba <strong>Unidades</strong> de cada empreendimento no CRM, preencha o campo <strong>DWV ID</strong> de cada unidade com o ID correspondente no DWV</p>
          <p><strong>5.</strong> Teste enviando uma alteração de status no DWV e verifique se o CRM atualiza</p>
        </div>
      </details>
    </div>
  );
}

/* ── Painéis curtos (Meta Lead Ads, Google Lead Form, portais) ─────── */
function MetaLeadsPainel() {
  const dominio = window.location.origin;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Login com o Facebook: escolha a página e os leads dos formulários de anúncio do Facebook e Instagram caem direto no funil.</p>
      <MetaConnectButton onConectado={() => {}} />
      <details className="group">
        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors list-none flex items-center gap-1">
          <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
          Precisa configurar o App do Facebook primeiro?
        </summary>
        <div className="mt-3 text-xs text-gray-500 space-y-1.5 bg-gray-50 rounded-lg p-3 leading-relaxed">
          <p><span className="font-medium">1.</span> Acesse <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">developers.facebook.com</a> e crie um App do tipo <strong>Business</strong></p>
          <p><span className="font-medium">2.</span> Adicione os produtos: <strong>Facebook Login</strong>, <strong>Webhooks</strong> e <strong>Lead Ads</strong></p>
          <p><span className="font-medium">3.</span> Em <em>Facebook Login → Configurações</em>, adicione como URI de redirecionamento OAuth:</p>
          <CopyLinha texto={`${dominio}/api/meta/callback`} />
          <p><span className="font-medium">4.</span> Copie o <strong>App ID</strong> e <strong>App Secret</strong> para o arquivo <code className="bg-gray-200 px-1 rounded">.env</code> do servidor (variáveis <code>META_APP_ID</code> e <code>META_APP_SECRET</code>)</p>
          <p><span className="font-medium">5.</span> Reinicie o backend após editar o .env</p>
        </div>
      </details>
    </div>
  );
}

function GoogleLeadFormPainel() {
  return (
    <div className="space-y-3 text-sm">
      <p className="text-gray-500">Formulários de lead dos anúncios do Google Ads entram no CRM por webhook. No Google Ads, edite o recurso de formulário de lead → <em>Integração de webhook</em> e informe:</p>
      <div><p className="text-xs text-gray-500">URL do webhook</p><CopyLinha texto={`${window.location.origin}/api/webhooks/google`} /></div>
      <p className="text-xs text-gray-400">Use "Enviar dados de teste" no Google Ads para conferir: o lead de teste aparece no funil.</p>
    </div>
  );
}

const PORTAIS = {
  chaves_na_mao: { env: 'CHAVES_API_KEY e CHAVES_API_URL', doc: 'Acesse app.chavesnamao.com.br → Integrações → API Key' },
  olx: { env: 'OLX_CLIENT_ID e OLX_CLIENT_SECRET', doc: 'Acesse developer.olx.com.br → Meus Apps → Credenciais' },
  sub100: { env: 'SUB100_API_KEY e SUB100_API_URL', doc: 'Acesse painel.sub100.com.br → Configurações → API' },
  zap: { env: 'ZAP_API_KEY', doc: 'Acesse grupozap.com.br/api → solicite acesso de parceiro' },
  viva_real: { env: 'VIVA_REAL_API_KEY', doc: 'Acesse grupozap.com.br/api → solicite acesso de parceiro (Viva Real)' },
};

function PortalPainel({ id }) {
  const p = PORTAIS[id];
  return (
    <div className="space-y-3 text-sm">
      <ol className="list-decimal pl-5 space-y-2 text-gray-600">
        <li>{p.doc}.</li>
        <li>As credenciais ficam no <code className="bg-gray-100 px-1 rounded">.env</code> do servidor (<code>{p.env}</code>) — peça ao suporte para cadastrar e reiniciar o backend.</li>
        <li>Publique as unidades na tela <Link to="/portais" className="text-blue-600 underline">Portais</Link> ou no cadastro de cada imóvel.</li>
      </ol>
    </div>
  );
}

/* ── Catálogo ──────────────────────────────────────────────────────── */
const CATEGORIAS = [
  { id: 'anuncios', nome: 'Anúncios e captação de leads', desc: 'Leads dos anúncios caem no funil; o Gestor de Tráfego lê as contas e devolve conversões.' },
  { id: 'google', nome: 'Google', desc: 'Site, buscas e Perfil da Empresa.' },
  { id: 'whatsapp', nome: 'WhatsApp', desc: 'Números usados pelos agentes, automações e atendimento.' },
  { id: 'marketing', nome: 'Marketing — imagens e vídeos', desc: 'Provedores usados pelo Marketing › Social Media.' },
  { id: 'unidades', nome: 'Gestão de unidades', desc: 'Status e valores das unidades sincronizados com sistemas externos.' },
  { id: 'portais', nome: 'Portais imobiliários', desc: 'Onde as unidades são anunciadas.' },
];

const TRAFEGO = '/marketing/trafego?aba=';

// status: ok · atencao · desligada · nao (não conectada) · info (sem estado, só instruções) · erro (não foi possível ler)
// resumo: [rótulo, valor][]
const INTEGRACOES = [
  {
    id: 'meta_leads', cat: 'anuncios', nome: 'Meta Lead Ads', tag: 'Leads dos formulários de anúncio do Facebook e Instagram.', cor: '#1877F2', icone: <FacebookIcon />,
    fonte: '/meta/status', painel: MetaLeadsPainel, tamanho: 'lg',
    ler: (d) => ({
      status: !d.conectado ? 'nao' : (!d.token_valido || d.ultimo_erro || !d.webhook_ativo) ? 'atencao' : 'ok',
      resumo: d.conectado ? [['Página', d.pagina?.nome || '—'], ['Webhook', d.webhook_ativo ? 'ativo' : 'desligado'], ['Último lead', quando(d.ultimo_lead) || 'nenhum ainda']] : [],
      aviso: d.ultimo_erro || (d.conectado && !d.token_valido ? 'Token do Facebook expirou — conecte de novo.' : null),
    }),
  },
  {
    id: 'meta_ads', cat: 'anuncios', nome: 'Meta Ads — Gestor de Tráfego', tag: 'Conta de anúncios, conversões (CAPI) e propostas do gestor.', cor: '#0866FF', sigla: 'MA',
    fonte: '/trafego/config', href: `${TRAFEGO}config`,
    ler: (d) => ({
      status: !d.configurado ? 'nao' : d.ultimo_sync_erro ? 'atencao' : 'ok',
      resumo: d.configurado ? [['Conta', d.ad_account_id], ['CAPI', d.capi_modo], ['Última leitura', quando(d.ultimo_sync_em) || 'ainda não leu']] : [],
      aviso: d.ultimo_sync_erro,
    }),
  },
  {
    id: 'google_ads', cat: 'anuncios', nome: 'Google Ads', tag: 'Campanhas, custos e envio de conversões offline.', cor: '#FBBC04', sigla: 'GA', siglaCor: '#3c4043',
    fonte: '/trafego/google-ads', href: `${TRAFEGO}google_ads`,
    ler: (d) => ({
      status: !d.conectado ? 'nao' : d.ultimo_sync_erro ? 'atencao' : 'ok',
      resumo: d.conectado ? [['Conta', d.conta_nome || d.customer_id || 'falta escolher'], ['Conversões', d.envio_conversoes_ok ? 'enviando' : 'desligadas'], ['Última leitura', quando(d.ultimo_sync_em) || 'ainda não leu']] : [],
      aviso: d.ultimo_sync_erro,
    }),
  },
  {
    id: 'google_leadform', cat: 'anuncios', nome: 'Google Ads — Lead Form', tag: 'Leads dos formulários de anúncio do Google via webhook.', cor: '#34A853', sigla: 'LF',
    painel: GoogleLeadFormPainel, ler: () => ({ status: 'info', resumo: [['Recebe por', 'webhook']] }),
  },
  {
    id: 'google', cat: 'google', nome: 'Search Console e Analytics', tag: 'SEO do site, buscas e visitas (GA4).', cor: '#4285F4', sigla: 'G',
    fonte: '/trafego/google', href: `${TRAFEGO}google`,
    ler: (d) => ({
      status: !d.conectado ? 'nao' : d.ultimo_sync_erro ? 'atencao' : 'ok',
      resumo: d.conectado ? [['Conta', d.email], ['Sites', (d.sites || []).map((s) => s.dominio).join(', ') || 'nenhum'], ['Última leitura', quando(d.ultimo_sync_em) || 'ainda não leu']] : [],
      aviso: d.ultimo_sync_erro,
    }),
  },
  {
    id: 'google_perfil', cat: 'google', nome: 'Perfil da Empresa', tag: 'Avaliações e dados do Google Maps.', cor: '#EA4335', sigla: 'PE',
    fonte: '/trafego/google-perfil', href: `${TRAFEGO}google_perfil`,
    ler: (d) => ({
      status: !d.conectado ? 'nao' : d.local && !d.ultimo_sync_erro ? 'ok' : 'atencao',
      resumo: d.conectado ? [['Conta', d.email], ['Perfil', d.titulo || (d.api_estado === 'aguardando_aprovacao' ? 'aguardando aprovação do Google' : 'falta escolher')]] : [],
      aviso: d.ultimo_sync_erro,
    }),
  },
  {
    id: 'whatsapp', cat: 'whatsapp', nome: 'WhatsApp (Evolution)', tag: 'Instâncias por QR Code dos agentes e corretores.', cor: '#25D366', sigla: 'WA',
    fonte: '/whatsapp', href: '/whatsapp',
    ler: (d) => {
      const lista = Array.isArray(d) ? d : [];
      const on = lista.filter((i) => i.connectionStatus === 'open');
      return {
        status: !lista.length ? 'nao' : on.length === lista.length ? 'ok' : on.length ? 'atencao' : 'desligada',
        resumo: lista.length ? [['Instâncias', `${on.length} de ${lista.length} conectadas`], ['Desconectadas', lista.filter((i) => i.connectionStatus !== 'open').map((i) => i.name).join(', ') || 'nenhuma']] : [],
      };
    },
  },
  {
    id: 'whatsapp_oficial', cat: 'whatsapp', nome: 'WhatsApp Oficial (Cloud API)', tag: 'Números da API oficial da Meta (modelos e campanhas).', cor: '#128C7E', sigla: 'WO',
    fonte: '/whatsapp-oficial', href: '/whatsapp-oficial',
    ler: (d) => {
      const lista = Array.isArray(d) ? d : [];
      const on = lista.filter((n) => n.status === 'ativo');
      return {
        status: !lista.length ? 'nao' : on.length === lista.length ? 'ok' : on.length ? 'atencao' : 'desligada',
        resumo: lista.map((n) => [n.nome, n.phone_display || n.phone_number_id]),
      };
    },
  },
  {
    id: 'higgsfield', cat: 'marketing', nome: 'Higgsfield', tag: 'Imagens e vídeos pela CLI oficial no servidor.', cor: '#111827', sigla: 'HF',
    fonte: '/marketing/imagens/higgsfield', painel: HiggsfieldPainel,
    ler: (d) => ({
      status: d.conectado ? 'ok' : 'nao',
      resumo: d.conectado ? [['Conta', d.email], ['Plano', d.plano || '—'], ['Créditos', d.creditos ?? '—']] : [],
      aviso: d.conectado ? null : d.erro,
    }),
  },
  {
    id: 'kling', cat: 'marketing', nome: 'Kling AI', tag: 'Imagens e vídeos por login OAuth (MCP oficial).', cor: '#7C3AED', sigla: 'KL',
    fonte: '/marketing/imagens/kling', painel: KlingPainel,
    ler: (d) => ({ status: d.conectado ? 'ok' : 'nao', resumo: d.conectado ? [['Conta', 'conectada']] : [], aviso: d.conectado ? null : d.erro }),
  },
  {
    id: 'dwv', cat: 'unidades', nome: 'DWV App', tag: 'Status e valores das unidades por webhook e sincronização.', cor: '#4F46E5', sigla: 'DW',
    fonte: '/dwv/config', painel: DwvPainel, tamanho: 'lg',
    ler: (d) => ({ status: !d.webhook_token ? 'nao' : d.ativo ? 'ok' : 'desligada', resumo: [['Webhook', d.ativo ? 'ativo' : 'desligado'], ['API', d.dwv_api_url || '—']] }),
  },
  ...[['chaves_na_mao', 'Chaves na Mão', '#E5173F', 'CM'], ['olx', 'OLX', '#6E0AD6', 'OLX'], ['sub100', 'Sub100', '#6D5BD0', 'S1'], ['zap', 'Zap Imóveis', '#FF6B00', 'ZAP'], ['viva_real', 'Viva Real', '#1E88E5', 'VR']].map(([id, nome, cor, sigla]) => ({
    id, cat: 'portais', nome, tag: 'Publicação das unidades no portal.', cor, sigla,
    fonte: '/portais/status', painel: () => <PortalPainel id={id} />, href: '/portais',
    ler: (d) => {
      const meus = (Array.isArray(d) ? d : []).filter((r) => r.portal === id);
      const pub = meus.filter((r) => r.status === 'publicado').length;
      const erros = meus.filter((r) => r.status === 'erro').length;
      return {
        status: !meus.length ? 'nao' : erros ? 'atencao' : 'ok',
        resumo: meus.length ? [['Publicadas', pub], ['Com erro', erros]] : [],
      };
    },
  })),
];

const STATUS = {
  ok: { label: 'Conectada', cls: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  atencao: { label: 'Atenção', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  desligada: { label: 'Desligada', cls: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
  nao: { label: 'Não conectada', cls: 'bg-white text-gray-500 border-gray-200', dot: 'bg-gray-300' },
  info: { label: 'Disponível', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-400' },
  erro: { label: 'Sem acesso', cls: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-400' },
  carregando: { label: 'Carregando', cls: 'bg-white text-gray-400 border-gray-200', dot: 'bg-gray-200 animate-pulse' },
};

function Selo({ it, size = 'md' }) {
  const box = size === 'md' ? 'w-11 h-11 text-sm' : 'w-9 h-9 text-xs';
  return (
    <span className={`${box} rounded-lg flex items-center justify-center flex-shrink-0 font-bold`} style={{ background: it.cor, color: it.siglaCor || '#fff' }} aria-hidden>
      {it.icone || it.sigla}
    </span>
  );
}

function Estado({ s }) {
  const c = STATUS[s] || STATUS.nao;
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${c.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.label}</span>;
}

function Cartao({ it, info, onAbrir }) {
  const s = info?.status || 'carregando';
  return (
    <li className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Selo it={it} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 leading-tight">{it.nome}</p>
          <p className="text-xs text-gray-500 mt-0.5">{it.tag}</p>
        </div>
        <Estado s={s} />
      </div>
      {info?.resumo?.length > 0 && (
        <dl className="space-y-1 text-sm">
          {info.resumo.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3"><dt className="text-gray-500 flex-shrink-0">{k}</dt><dd className="text-right text-gray-800 truncate" title={String(v)}>{v}</dd></div>
          ))}
        </dl>
      )}
      {info?.aviso && <p className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-2.5 py-1.5 line-clamp-3">{info.aviso}</p>}
      <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-2">
        {it.painel ? (
          <button type="button" onClick={onAbrir} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
            <Settings size={14} /> {s === 'nao' ? 'Conectar' : 'Gerenciar'}
          </button>
        ) : (
          <Link to={it.href} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
            <ExternalLink size={14} /> {s === 'nao' ? 'Conectar' : 'Gerenciar'}
          </Link>
        )}
        {it.painel && it.href && <Link to={it.href} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">Abrir tela →</Link>}
      </div>
    </li>
  );
}

/* ── Componente principal ──────────────────────────────────────────── */
export default function Integracoes() {
  const [infos, setInfos] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('todas'); // todas | conectadas | atencao | nao
  // volta de login OAuth (?meta=… / ?kling=…): reabre o painel certo para concluir
  const [aberto, setAberto] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get('meta') ? 'meta_leads' : q.get('kling') ? 'kling' : null;
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    const fontes = [...new Set(INTEGRACOES.map((i) => i.fonte).filter(Boolean))];
    const res = await Promise.allSettled(fontes.map((f) => api.get(f).then((r) => r.data.data)));
    const porFonte = Object.fromEntries(fontes.map((f, i) => [f, res[i]]));
    const novo = {};
    for (const it of INTEGRACOES) {
      const r = it.fonte ? porFonte[it.fonte] : { status: 'fulfilled', value: {} };
      if (r.status === 'rejected') {
        const st = r.reason?.response?.status;
        novo[it.id] = { status: 'erro', resumo: [], aviso: st === 403 ? 'Seu perfil não tem acesso a esta integração.' : (r.reason?.response?.data?.error || r.reason?.message) };
      } else {
        try { novo[it.id] = it.ler(r.value || {}); } catch (_) { novo[it.id] = { status: 'erro', resumo: [] }; }
      }
    }
    setInfos(novo);
    setCarregando(false);
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const termo = busca.trim().toLowerCase();
  const visiveis = useMemo(() => INTEGRACOES.filter((it) => {
    if (termo && !`${it.nome} ${it.tag}`.toLowerCase().includes(termo)) return false;
    const s = infos[it.id]?.status;
    if (filtro === 'conectadas') return s === 'ok' || s === 'atencao';
    if (filtro === 'atencao') return s === 'atencao' || s === 'erro';
    if (filtro === 'nao') return s === 'nao' || s === 'desligada';
    return true;
  }), [termo, filtro, infos]);

  const conta = (f) => INTEGRACOES.filter((it) => f(infos[it.id]?.status)).length;
  const filtros = [
    ['todas', 'Todas', INTEGRACOES.length],
    ['conectadas', 'Conectadas', conta((s) => s === 'ok' || s === 'atencao')],
    ['atencao', 'Precisam de atenção', conta((s) => s === 'atencao' || s === 'erro')],
    ['nao', 'Não conectadas', conta((s) => s === 'nao' || s === 'desligada')],
  ];

  const itAberto = INTEGRACOES.find((i) => i.id === aberto);
  const Painel = itAberto?.painel;
  const fechar = () => {
    setAberto(null);
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
    carregar();
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Integrações</h1>
          <p className="text-sm text-gray-500 mt-1">Os serviços conectados ao CRM. Cada cartão mostra se está funcionando; em <strong>Gerenciar</strong> você conecta, edita ou desliga.</p>
        </div>
        <button onClick={carregar} disabled={carregando} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar integração" aria-label="Buscar integração"
            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-secondary" />
        </div>
        <div className="flex flex-wrap gap-2">
          {filtros.map(([k, label, n]) => (
            <button key={k} type="button" onClick={() => setFiltro(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filtro === k ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label} <span className={filtro === k ? 'text-white/70' : 'text-gray-400'}>{n}</span>
            </button>
          ))}
        </div>
      </div>

      {CATEGORIAS.map((cat) => {
        const itens = visiveis.filter((it) => it.cat === cat.id);
        if (!itens.length) return null;
        return (
          <section key={cat.id}>
            <h2 className="text-base font-semibold text-gray-800">{cat.nome}</h2>
            <p className="text-sm text-gray-500 mb-3">{cat.desc}</p>
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {itens.map((it) => <Cartao key={it.id} it={it} info={infos[it.id]} onAbrir={() => setAberto(it.id)} />)}
            </ul>
          </section>
        );
      })}
      {!visiveis.length && <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-sm text-gray-500">Nenhuma integração encontrada.</div>}

      <Modal isOpen={!!Painel} onClose={fechar} title={itAberto?.nome} size={itAberto?.tamanho || 'lg'}>
        {Painel && <Painel />}
      </Modal>
    </div>
  );
}
